import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PermissaoDoConector } from '@orbind/core'
import { api, apiBase, webTokenStorage } from '../../lib/api.ts'

/**
 * Conector do Claude (SPEC-20261103-1818) — duas superfícies, dois donos.
 *
 * `/ai-connectors/me*` é da PESSOA: tokens, pacote, skill, minhas ações.
 * `/ai-connectors/admin*` é do ADMINISTRADOR: quem pode e com que permissões.
 * Chaves de cache separadas porque liberar alguém não muda os meus tokens, e
 * gerar um token não muda a lista do administrador (só o contador — por isso
 * o `invalidate` cruzado em quem cria/revoga token).
 */

export type TokenDoConector = {
  id: string
  name: string
  last4: string
  createdAt: string
  lastUsedAt: string | null
  /** `pessoal` (gerado na tela) ou `oauth` (claude.ai / organização, SPEC-20261103-1822). */
  kind: 'pessoal' | 'oauth'
  expiresAt: string | null
  clientName: string | null
  clientUri: string | null
}

export type MeuConector = {
  disponivel: boolean
  liberado: boolean
  permissoes: PermissaoDoConector[]
  tokens: TokenDoConector[]
  url: string
  comandoDoClaudeCode: string
  versao: string
  admin: boolean
}

export type EventoDoConector = {
  id: string
  tool: string
  summary: string
  createdAt: string
  workItemId: string | null
  workItemCode: string | null
  memberName: string
}

export type PessoaDoConector = {
  memberId: string
  name: string
  email: string
  ativo: boolean
  liberado: boolean
  permissoes: PermissaoDoConector[]
  liberadoEm: string | null
  tokensAtivos: number
  ultimoUso: string | null
}

const comoJson = <T,>(p: Promise<unknown>): Promise<T> => p as Promise<T>

const CHAVE_ME = ['ai-connectors', 'me']
const CHAVE_MEUS_EVENTOS = ['ai-connectors', 'me', 'eventos']
const CHAVE_ADMIN = ['ai-connectors', 'admin']
const CHAVE_EVENTOS_ADMIN = ['ai-connectors', 'admin', 'eventos']

async function lerErro(resposta: Response, padrao: string): Promise<never> {
  const corpo = (await resposta.json().catch(() => ({}))) as { error?: { message?: string } }
  throw new Error(corpo.error?.message ?? padrao)
}

/* ------------------------------------------------------------- pessoa --- */

export function useMeuConector() {
  return useQuery({
    queryKey: CHAVE_ME,
    queryFn: async () => {
      const r = await api['ai-connectors'].me.$get()
      if (!r.ok) await lerErro(r, 'Não foi possível carregar o conector.')
      return comoJson<MeuConector>(r.json())
    },
  })
}

export function useGerarToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const r = await api['ai-connectors'].me.tokens.$post({ json: { name } })
      if (!r.ok) await lerErro(r, 'Não foi possível gerar o token.')
      return comoJson<{ token: string; item: TokenDoConector }>(r.json())
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE_ME })
      void qc.invalidateQueries({ queryKey: CHAVE_ADMIN })
    },
  })
}

export function useRevogarToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api['ai-connectors'].me.tokens[':id'].$delete({ param: { id } })
      if (!r.ok) await lerErro(r, 'Não foi possível revogar o token.')
      return r.json()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE_ME })
      void qc.invalidateQueries({ queryKey: CHAVE_ADMIN })
    },
  })
}

export function useMeusEventos() {
  return useQuery({
    queryKey: CHAVE_MEUS_EVENTOS,
    queryFn: async () => comoJson<{ eventos: EventoDoConector[] }>((await api['ai-connectors'].me.eventos.$get()).json()),
  })
}

/**
 * Download com sessão. `<a href>` não manda o Authorization, então o arquivo
 * vem por fetch e vira um link de blob — o nome vem de nós, não do servidor.
 */
export async function baixarDoConector(caminho: 'pacote.mcpb' | 'skill.zip', nomeDoArquivo: string): Promise<void> {
  const token = webTokenStorage.get()
  const r = await fetch(`${apiBase()}/ai-connectors/me/${caminho}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!r.ok) await lerErro(r, 'Não foi possível baixar o arquivo.')
  const url = URL.createObjectURL(await r.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = nomeDoArquivo
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/* ------------------------------------------------------ administrador --- */

export function usePessoasDoConector() {
  return useQuery({
    queryKey: CHAVE_ADMIN,
    queryFn: async () => {
      const r = await api['ai-connectors'].admin.$get()
      if (!r.ok) await lerErro(r, 'Não foi possível carregar as pessoas.')
      return comoJson<{ pessoas: PessoaDoConector[]; permissoes: PermissaoDoConector[]; padrao: PermissaoDoConector[] }>(
        r.json(),
      )
    },
  })
}

export function useLiberarPessoa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: { memberId: string; liberado: boolean; permissoes?: PermissaoDoConector[] }) => {
      const r = await api['ai-connectors'].admin[':memberId'].$put({
        param: { memberId: entrada.memberId },
        json: { liberado: entrada.liberado, permissoes: entrada.permissoes },
      })
      if (!r.ok) await lerErro(r, 'Não foi possível salvar a liberação.')
      return r.json()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAVE_ADMIN })
      // a própria pessoa pode estar olhando a tela dela
      void qc.invalidateQueries({ queryKey: CHAVE_ME })
    },
  })
}

export function useEventosDoWorkspace() {
  return useQuery({
    queryKey: CHAVE_EVENTOS_ADMIN,
    queryFn: async () =>
      comoJson<{ eventos: EventoDoConector[] }>((await api['ai-connectors'].admin.eventos.$get()).json()),
  })
}

/** Texto curto para a lista de ações: "há 5 min", "ontem 14:02", "03/11 09:10". */
export function quandoFoi(iso: string | null): string {
  if (!iso) return 'nunca'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  if (diff < 60_000) return 'agora'
  if (diff < 3_600_000) return `há ${Math.floor(diff / 60_000)} min`
  const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d)
  const hoje = new Date()
  if (d.toDateString() === hoje.toDateString()) return `hoje ${hora}`
  const ontem = new Date(hoje)
  ontem.setDate(hoje.getDate() - 1)
  if (d.toDateString() === ontem.toDateString()) return `ontem ${hora}`
  return `${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)} ${hora}`
}
